import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { MutationListResponse, MutationResponse } from 'global';

import { bpfDepartmentAPI } from './index';
import { BPFAPINameSpase, Department } from '../../types';

export function useBPFDepartments() {
  const { showNotification } = useNotifications();

  const [onCreate, { loading: isCreating }] = useMutation<BPFAPINameSpase<MutationResponse<Department>>>(
    bpfDepartmentAPI.create
  );

  const handleCreateDepartment = useCallback(
    async (dto: Record<string, any>) => {
      try {
        const { data } = await onCreate({ variables: { input: dto } });

        if (!data?.bpf?.result?.success) {
          return showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        }

        showNotification('Department successfully created');
      } catch (error: any) {
        showNotification(error?.message ?? 'Something went wrong', { type: 'error' });
      }
    },
    [onCreate, showNotification]
  );

  const [onUpdate, { loading: isUpdating }] = useMutation<BPFAPINameSpase<MutationResponse<Department>>>(
    bpfDepartmentAPI.update
  );

  const handleUpdateDepartment = useCallback(
    async (dto: Record<string, any>) => {
      try {
        const { data } = await onUpdate({ variables: { input: dto } });

        if (!data?.bpf?.result?.success) {
          return showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        }

        showNotification('Department successfully updated!');
      } catch (error: any) {
        showNotification(error?.message ?? 'Something went wrong', { type: 'error' });
      }
    },
    [onUpdate, showNotification]
  );

  const [onUpsert, { loading: isUpserting }] = useMutation<BPFAPINameSpase<MutationListResponse<Department>>>(
    bpfDepartmentAPI.upsert
  );

  const handleUpsertDepartments = useCallback(
    async (dto: Record<string, any>[]) => {
      try {
        const { data } = await onUpsert({ variables: { input: { list: dto } } });

        if (!data?.bpf?.result?.success) {
          return showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        }

        showNotification('Departments successfully updated!');
      } catch (error: any) {
        showNotification(error?.message ?? 'Something went wrong', { type: 'error' });
      }
    },
    [onUpsert, showNotification]
  );

  const [onDelete, { loading: isDeleting }] = useMutation<BPFAPINameSpase<MutationResponse<Department>>>(
    bpfDepartmentAPI.delete
  );

  const handleDeleteDepartment = useCallback(
    async (id: string) => {
      try {
        const { data } = await onDelete({ variables: { input: { id } } });

        if (!data?.bpf?.result?.success) {
          return showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        }

        showNotification('Department successfully deleted!');
      } catch (error: any) {
        showNotification(error?.message ?? 'Something went wrong', { type: 'error' });
      }
    },
    [onDelete, showNotification]
  );

  return {
    isCreating,
    isUpdating,
    isDeleting,
    isUpserting,
    handleCreateDepartment,
    handleUpdateDepartment,
    handleDeleteDepartment,
    handleUpsertDepartments,
  };
}
